import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input, Modal, ModalContent, ModalHeader } from "semantic-ui-react";

import { SortableList } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components";
import { AddInterventionPendingChecklist } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components/AddInterventionPendingChecklist";
import "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/InterventionChecklistsModal.scss";

import type { UniqueIdentifier } from "@dnd-kit/core";
import type { Checklist, InterventionKeywordPendingChecklist } from "models";
import type { InterventionPendingChecklistsGrouped } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/hooks";

export type SortableInterventionPendingChecklists = { id: UniqueIdentifier } & InterventionKeywordPendingChecklist;

type InterventionChecklistsModalProps = {
  selectedInterventionKeyword: string | null | undefined;
  checklists?: Checklist[];
  interventionPendingChecklistsGrouped?: InterventionPendingChecklistsGrouped[];
  onClose: () => void;
  onSetSelectedInterventionKeyword: (keyword: string) => void;
};

export const InterventionChecklistsModal = ({
  checklists,
  selectedInterventionKeyword,
  interventionPendingChecklistsGrouped,
  onClose,
  onSetSelectedInterventionKeyword
}: InterventionChecklistsModalProps) => {
  const { t } = useTranslation();

  const [interventionKeyword, setInterventionKeyword] = useState<string | null>(selectedInterventionKeyword ?? null);
  const [sortableInterventionPendingChecklists, setSortableInterventionPendingChecklists] = useState<SortableInterventionPendingChecklists[]>([]);

  const selectedInterventionDetails = interventionPendingChecklistsGrouped?.find(ipc => ipc.keyword === selectedInterventionKeyword);

  useEffect(() => {
    const selectedInterventionChecklists = selectedInterventionDetails?.checklists ?? [];

    if (selectedInterventionChecklists.length) {
      setInterventionKeyword(selectedInterventionKeyword ?? null);
      const offset = selectedInterventionChecklists.length;
      setSortableInterventionPendingChecklists(selectedInterventionChecklists.map(pc => ({ ...pc, id: offset + pc.ordering })));
      return;
    }

    setInterventionKeyword(null);
    setSortableInterventionPendingChecklists([]);
  }, [selectedInterventionKeyword, selectedInterventionDetails]);

  const handleClose = () => {
    onClose();
    setSortableInterventionPendingChecklists([]);
    setInterventionKeyword(null);
  };

  return (
    <Modal open={selectedInterventionKeyword !== undefined} closeOnDimmerClick={false} onClose={handleClose} className="InterventionChecklistsModal">
      <ModalHeader className="header-container">
        {selectedInterventionKeyword
          ? `${t("v8_add_intervention_checklists_to", "Add Intervention Checklists to")} ${selectedInterventionKeyword}`
          : `${t("v8_add_intervention_checklists", "Add Intervention Checklists")}`}
        <div className="close-btn">
          <Button className="modal-close-btn" basic onClick={handleClose}>
            <Icon className="xmark grey" />
          </Button>
        </div>
      </ModalHeader>
      <ModalContent>
        <div className="intervention-title-container">
          <Input
            placeholder={t("v8_intervention_title", "Intervention Title")}
            disabled={Boolean(selectedInterventionKeyword)}
            value={interventionKeyword ?? ""}
            onChange={(_, data) => setInterventionKeyword(data.value)}
            fluid
          />
        </div>
        <div className="sortable-list-container">
          <SortableList
            checklists={checklists}
            sortableInterventionPendingChecklists={sortableInterventionPendingChecklists}
            onClose={onClose}
            setSortableInterventionPendingChecklists={setSortableInterventionPendingChecklists}
          />
        </div>
        <AddInterventionPendingChecklist
          keyword={interventionKeyword}
          checklists={checklists}
          sortableInterventionPendingChecklists={sortableInterventionPendingChecklists}
          onSetSelectedInterventionKeyword={onSetSelectedInterventionKeyword}
          pendingChecklistsGrouped={interventionPendingChecklistsGrouped}
        />
      </ModalContent>
    </Modal>
  );
};
